import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    MapPin,
    Plus,
    Pencil,
    Phone,
    Star,
    RefreshCw,
    AlertCircle,
} from "lucide-react";
import { useEditor } from "../../../../App.jsx";
import { describirDireccion } from "../../../../domain/direcciones/index.js";

const ListasDirecciones = ({ setEditor, setEditorType, setEditorID }) => {
    const [direcciones, setDirecciones] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const { stateEditor, setStateEditor } = useEditor();

    // Abre el editor de direcciones. Soporta tanto el flujo por props (legacy)
    // como el flujo por contexto (Profile2 + Editor).
    const openDireccionEditor = (id) => {
        setEditorType?.("directions");
        setEditorID?.(id);
        setEditor?.(true);
        setStateEditor({
            ...stateEditor,
            editor: true,
            editorType: "directions",
            editorID: id,
        });
    };

    useEffect(() => {
        fetchDirecciones();
    }, []);

    const fetchDirecciones = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await axios.get("api/direcciones/");
            setDirecciones(response.data);
        } catch (error) {
            console.error("Error fetching direcciones:", error);
            setError("No se pudieron cargar las direcciones");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchDirecciones();
        setIsRefreshing(false);
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex gap-4 overflow-x-auto pb-2">
                    {[...Array(3)].map((_, index) => (
                        <div key={index} className="flex-shrink-0 w-80">
                            <div className="animate-pulse bg-base-300 h-32 rounded-lg"></div>
                        </div>
                    ))}
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="w-8 h-8 text-error mb-4" />
                    <p className="text-base-content/70 mb-4">{error}</p>
                    <button
                        className="btn btn-outline btn-sm gap-2"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <RefreshCw size={14} />
                        )}
                        Reintentar
                    </button>
                </div>
            );
        }

        return (
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                <DireccionCardAdd onAdd={() => openDireccionEditor("add")} />
                {direcciones && direcciones.map((direccion, index) => (
                    <DireccionCard
                        key={direccion.id || index}
                        direccion={direccion}
                        onEdit={() => openDireccionEditor(direccion.id)}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="glassCard rounded-2xl p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3 border-b border-base-300 pb-3">
                {/* Sección principal - título e icono */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                        <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-lg sm:text-xl font-bold text-base-content truncate">
                            Direcciones de envío
                        </h2>
                        <p className="text-base-content/70 text-xs sm:text-sm truncate">
                            Gestiona tus direcciones para pedidos
                        </p>
                    </div>
                </div>

                {/* Sección de controles */}
                <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
                    {/* Contador de direcciones */}
                    {!isLoading && !error && (
                        <div className="badge badge-outline text-xs sm:text-sm">
                            {direcciones.length}
                            <span className="ml-1">
                                <span className="hidden sm:inline">
                                    {direcciones.length === 1 ? "dirección" : "direcciones"}
                                </span>
                                <span className="sm:hidden">
                                    {direcciones.length === 1 ? "dir." : "dirs."}
                                </span>
                            </span>
                        </div>
                    )}

                    {/* Botón de refresh */}
                    <button
                        className="btn btn-ghost btn-sm btn-circle flex-shrink-0"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        title="Actualizar direcciones"
                    >
                        {isRefreshing ? (
                            <span className="loading loading-spinner loading-xs"></span>
                        ) : (
                            <RefreshCw size={14} />
                        )}
                    </button>
                </div>
            </div>

            {/* Contenido principal */}
            <div className="overflow-hidden">
                {renderContent()}
            </div>

            {/* Estado vacío */}
            {!isLoading && !error && direcciones.length === 0 && (
                <div className="text-center py-8">
                    <MapPin className="w-16 h-16 mx-auto text-base-content/30 mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        No tienes direcciones guardadas
                    </h3>
                    <p className="text-base-content/70 mb-6 max-w-md mx-auto">
                        Añade direcciones de envío para agilizar tus pedidos futuros.
                    </p>
                </div>
            )}
        </div>
    );
};

function DireccionCard({ direccion, onEdit }) {
    return (
        <div
            className="flex-shrink-0 w-80 card bg-base-100 shadow-md hover:shadow-xl transition-shadow duration-200 cursor-pointer overflow-hidden"
            onClick={onEdit}
            title="Editar dirección"
        >
            {/* Franja de acento: primaria si es la predeterminada */}
            <div className={`h-1 ${direccion.predeterminado ? "bg-primary" : "bg-base-300"}`} />

            <div className="card-body p-5 gap-3">
                {/* Nombre + badge */}
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-base-content truncate">
                        {direccion.nombre_contacto || "Dirección"}
                    </h3>
                    {direccion.predeterminado && (
                        <span className="badge badge-primary badge-sm gap-1 flex-shrink-0">
                            <Star size={10} /> Predeterminada
                        </span>
                    )}
                </div>

                {/* Dirección */}
                <div className="flex items-start gap-2.5 text-sm">
                    <MapPin size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                        <p className="text-base-content/90 line-clamp-2">
                            {direccion.direccion}
                            {direccion.apartamento && `, ${direccion.apartamento}`}
                        </p>
                        <p className="text-base-content/60">
                            {describirDireccion(direccion)}
                        </p>
                    </div>
                </div>

                {/* Teléfono */}
                {direccion.telefono && (
                    <div className="flex items-center gap-2.5 text-sm">
                        <Phone size={16} className="text-primary flex-shrink-0" />
                        <span className="text-base-content/80 truncate">{direccion.telefono}</span>
                    </div>
                )}

                {/* Acción */}
                <div className="card-actions justify-end border-t border-base-200 pt-2 mt-auto">
                    <span className="btn btn-ghost btn-xs gap-1 text-primary">
                        <Pencil size={12} /> Editar
                    </span>
                </div>
            </div>
        </div>
    );
}

function DireccionCardAdd({ onAdd }) {
    return (
        <div
            className="flex-shrink-0 w-80 card bg-base-100/40 border-2 border-dashed border-base-300 hover:bg-base-100/80 transition-colors duration-200 cursor-pointer"
            onClick={onAdd}
        >
            <div className="card-body p-5 items-center justify-center text-center min-h-[180px] gap-2">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Plus className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold text-base-content">Añadir dirección</h3>
                <p className="text-sm text-base-content/60 leading-relaxed">
                    Crea una nueva dirección de envío para tus pedidos
                </p>
            </div>
        </div>
    );
}

export default ListasDirecciones;
